import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import SmsReportsApiService from "../../../../../infrastructure/api/smsReports";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import { isAdminUser } from "../../../../utils/authRoleHelpers";
import { initialFilterValues, PAGE_SUBTITLE, PAGE_TITLE } from "./smsMonitoring/constants";
import SmsDetailDialog from "./smsMonitoring/SmsDetailDialog";
import SmsMonitoringAlerts from "./smsMonitoring/SmsMonitoringAlerts";
import SmsMonitoringCharts from "./smsMonitoring/SmsMonitoringCharts";
import SmsMonitoringFilters from "./smsMonitoring/SmsMonitoringFilters";
import SmsMonitoringKpiCards from "./smsMonitoring/SmsMonitoringKpiCards";
import SmsMonitoringTable from "./smsMonitoring/SmsMonitoringTable";
import { buildSearchPayload, getQuickDateRange } from "./smsMonitoring/utils";

const SmsReports = () => {
  const isAdmin = isAdminUser();
  const hasExportExcel = useCheckPermission("excel");
  const hasExportPdf = useCheckPermission("pdf");

  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [smsTypes, setSmsTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [retryConfirm, setRetryConfirm] = useState({ open: false, id: null });
  const [retrying, setRetrying] = useState(false);

  const loadSmsTypes = useCallback(async () => {
    try {
      const response = await SmsReportsApiService.getSmsTypes();
      if (response?.isSuccess ?? response?.IsSuccess) {
        const raw = response.data ?? response.Data ?? [];
        setSmsTypes(
          raw.map((item) =>
            typeof item === "string"
              ? { key: item, label: item }
              : {
                  key: item.key ?? item.Key ?? "",
                  label: item.label ?? item.Label ?? item.key ?? item.Key ?? "",
                }
          )
        );
      }
    } catch (error) {
      console.error("Error loading SMS types:", error);
    }
  }, []);

  const loadDashboard = useCallback(async (filters = filterValues) => {
    const payload = buildSearchPayload(filters);
    setLoading(true);
    try {
      const [summaryRes, listRes] = await Promise.all([
        SmsReportsApiService.getMonitoringSummary(payload),
        SmsReportsApiService.getSmsReport(payload),
      ]);

      if (summaryRes?.isSuccess ?? summaryRes?.IsSuccess) {
        setSummary(summaryRes.data ?? summaryRes.Data ?? null);
      }

      if (listRes?.isSuccess ?? listRes?.IsSuccess) {
        const data = listRes.data ?? listRes.Data ?? {};
        const items = data.items ?? data.Items ?? (Array.isArray(data) ? data : []);
        setRows(items);
        setTotalCount(data.totalCount ?? data.TotalCount ?? items.length);
      }
    } catch (error) {
      toast.error("خطا در بارگذاری داشبورد پیامک");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filterValues]);

  useEffect(() => {
    loadSmsTypes();
    loadDashboard(initialFilterValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    const next = { ...filterValues, pageNumber: 1 };
    setFilterValues(next);
    loadDashboard(next);
  };

  const handleReset = () => {
    setFilterValues(initialFilterValues);
    loadDashboard(initialFilterValues);
  };

  const handleQuickDate = (preset) => {
    const range = getQuickDateRange(preset);
    const next = { ...filterValues, ...range, pageNumber: 1 };
    setFilterValues(next);
    loadDashboard(next);
  };

  const handlePageChange = (pageNumber, pageSize = filterValues.pageSize) => {
    const next = { ...filterValues, pageNumber, pageSize };
    setFilterValues(next);
    loadDashboard(next);
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      const response = await SmsReportsApiService.syncDeliveryStatuses(200);
      if (response?.isSuccess ?? response?.IsSuccess) {
        toast.success(response.message || "وضعیت پیامک‌ها بروزرسانی شد");
        await loadDashboard();
      } else {
        toast.error(response?.message || "خطا در بروزرسانی");
      }
    } catch {
      toast.error("خطا در ارتباط با سرور");
    } finally {
      setSyncing(false);
    }
  };

  const handleViewDetail = async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const response = await SmsReportsApiService.getSmsDetail(id);
      if (response?.isSuccess ?? response?.IsSuccess) {
        setDetailData(response.data ?? response.Data);
      } else {
        toast.error(response?.message || "خطا در دریافت جزئیات");
        setDetailOpen(false);
      }
    } catch {
      toast.error("خطا در دریافت جزئیات");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRefreshStatus = async (id) => {
    try {
      setRefreshingId(id);
      const response = await SmsReportsApiService.refreshSmsStatus(id);
      if (response?.isSuccess ?? response?.IsSuccess) {
        toast.success(response.message || "وضعیت بروزرسانی شد");
        await loadDashboard();
        if (detailOpen && detailData?.id === id) {
          await handleViewDetail(id);
        }
      } else {
        toast.error(response?.message || "بروزرسانی ناموفق");
      }
    } catch {
      toast.error("خطا در بروزرسانی وضعیت");
    } finally {
      setRefreshingId(null);
    }
  };

  const handleRetryConfirm = async () => {
    const id = retryConfirm.id;
    if (!id) return;
    try {
      setRetrying(true);
      const response = await SmsReportsApiService.retrySms(id);
      if (response?.isSuccess ?? response?.IsSuccess) {
        toast.success(response.message || "ارسال مجدد انجام شد");
        setRetryConfirm({ open: false, id: null });
        await loadDashboard();
      } else {
        toast.error(response?.message || "ارسال مجدد ناموفق بود");
      }
    } catch {
      toast.error("خطا در ارسال مجدد");
    } finally {
      setRetrying(false);
    }
  };

  const handleExport = async (type) => {
    try {
      setExporting(true);
      const payload = buildSearchPayload(filterValues);
      const response =
        type === "pdf"
          ? await SmsReportsApiService.exportPdf(payload)
          : await SmsReportsApiService.exportExcel(payload);

      if ((response?.isSuccess ?? response?.IsSuccess) && response?.data?.link) {
        window.open(response.data.link, "_blank");
      } else {
        toast.error(response?.message || "خطا در خروجی");
      }
    } catch {
      toast.error("خطا در تهیه خروجی");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {PAGE_TITLE}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {PAGE_SUBTITLE}
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleSyncAll}
                disabled={syncing || loading}
              >
                بروزرسانی وضعیت
              </Button>
              {hasExportExcel ? (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handleExport("excel")}
                  disabled={exporting || loading}
                >
                  Excel
                </Button>
              ) : null}
              {hasExportPdf ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => handleExport("pdf")}
                  disabled={exporting || loading}
                >
                  PDF
                </Button>
              ) : null}
            </Box>

            <SmsMonitoringFilters
              filterValues={filterValues}
              setFilterValues={setFilterValues}
              smsTypes={smsTypes}
              onSearch={handleSearch}
              onReset={handleReset}
              onQuickDate={handleQuickDate}
              loading={loading}
            />

            <Divider sx={{ my: 2 }} />

            <SmsMonitoringKpiCards summary={summary} />
            <SmsMonitoringAlerts alerts={summary?.alerts ?? summary?.Alerts ?? []} />
            <SmsMonitoringCharts summary={summary} />

            <SmsMonitoringTable
              rows={rows}
              totalCount={totalCount}
              pageNumber={filterValues.pageNumber}
              pageSize={filterValues.pageSize}
              loading={loading}
              isAdmin={isAdmin}
              onPageChange={handlePageChange}
              onViewDetail={handleViewDetail}
              onRefreshStatus={handleRefreshStatus}
              onRetry={(id) => setRetryConfirm({ open: true, id })}
            />
          </CardContent>
        </Card>
      </Grid>

      <SmsDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        data={detailData}
        loading={detailLoading}
        isAdmin={isAdmin}
        onRefreshStatus={handleRefreshStatus}
        onRetry={(id) => setRetryConfirm({ open: true, id })}
        refreshing={Boolean(refreshingId)}
        retrying={retrying}
      />

      <Dialog open={retryConfirm.open} onClose={() => setRetryConfirm({ open: false, id: null })}>
        <DialogTitle>ارسال مجدد پیامک</DialogTitle>
        <DialogContent>
          <DialogContentText>آیا از ارسال مجدد این پیامک اطمینان دارید؟</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryConfirm({ open: false, id: null })}>انصراف</Button>
          <Button color="warning" variant="contained" onClick={handleRetryConfirm} disabled={retrying}>
            ارسال مجدد
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SmsReports;
